import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React, { useState } from 'react';

import { QRCode } from '@components/qr-code';
import { Box, Flex } from '@components/grid';
import { Button } from '@components/button/new';
import { P, H4 } from '@components/typography/new';
import { Modal } from '@components/modal/new';
import { ShareButton } from '@components/share-button';
import { CopyTextButton } from '@components/copy-text-button';
import { BUTTON_VARIANTS } from '@components/button/constants';
import { ANALYTICS_EVENTS, colors } from '@nandosaus/constants';
import { useRootStore } from '@nandosaus/state-management';
import { useIsMinWidth } from '@hooks/use-is-min-width';
import { analytics } from '@analytics';
import { Icon } from '@components/icons';

const TabOrderShareModal = ({ modalOpen, handleModalClose }) => {
  const [showLeaveContent, setShowLeaveContent] = useState(false);

  const { GroupOrderStore } = useRootStore();
  const { leaveGroup, canLeaveGroup, name, shareLink } = GroupOrderStore;

  const handleLeaveGroup = async () => {
    await leaveGroup();
    analytics.track(ANALYTICS_EVENTS.GROUP_ORDER_GROUP_LEFT, { name });
    handleModalClose();
    setShowLeaveContent(false);
  };

  const closeModal = () => {
    handleModalClose();
    setShowLeaveContent(false);
  };

  const leaveGroupButton = (
    <Flex justifyContent="center">
      <Button
        type="button"
        height="55px"
        variant={BUTTON_VARIANTS.LINK}
        onClick={() => setShowLeaveContent(true)}
        px="8px"
        aria-label="Leave Group"
      >
        <Flex alignItems="center">
          <Icon name="share" fill={colors.primaryRed} aria-hidden="true" />
          <P ml="8px" variant="medium-link" textDecoration="underline" textTransform="none" aria-hidden="true">
            Leave Group
          </P>
        </Flex>
      </Button>
    </Flex>
  );

  const isDesktop = useIsMinWidth('lg');

  // Leave content view
  const leaveContent = (
    <Flex height="100%" width="100%" justifyContent="center" alignItems="center" position="relative">
      <Box width="100%" textAlign="center" position="relative" mb="32px">
        <H4 my="0">Leaving?</H4>
        <P variant="small" mt="8px">
          If so, your order will not be added to the group.
        </P>

        <Flex gap="12px" justifyContent="center" mt="24px" flexDirection={{ _: 'column', md: 'row' }}>
          <Button widthType="full" variant="secondary" size="tall" onClick={() => setShowLeaveContent(false)}>
            Stay
          </Button>
          <Button widthType="full" variant="primary" size="tall" onClick={handleLeaveGroup}>
            Leave Group
          </Button>
        </Flex>
      </Box>
    </Flex>
  );

  // Share content view
  const shareContent = (
    <Box position="relative" height="100%" mb="32px">
      <Box
        textAlign="center"
        position="relative"
        mb="32px"
        mx="32px"
        display="flex"
        flexDirection="column"
        alignItems="center"
      >
        {!!name && <H4>{name}</H4>}
        <P mb={{ md: '24px', _: '64px' }} variant="small">
          Invite others to the group by scanning the QR code or sharing the link!
        </P>
        <Box padding="32px" backgroundColor="white" borderRadius="24px" width="308px">
          {!!shareLink && <QRCode url={shareLink} size={240} />}
        </Box>
      </Box>
      {!!shareLink && (
        <CopyTextButton
          onClick={() => analytics.track(ANALYTICS_EVENTS.GROUP_ORDER_GROUP_SHARED, { groupName: name })}
          text={shareLink}
          label="Group order link"
        />
      )}
      <Flex flexWrap="wrap" mt="24px" justifyContent="center" alignItems="center" gap="16px 40px">
        {!!shareLink && (
          <ShareButton
            onClick={() => analytics.track(ANALYTICS_EVENTS.GROUP_ORDER_GROUP_SHARED, { groupName: name })}
            url={shareLink}
            buttonText="Share this order"
            title="Join my Nando's Group Order"
            pt="0"
          />
        )}
        {canLeaveGroup && leaveGroupButton}
      </Flex>
    </Box>
  );

  return (
    <Modal
      handleClose={closeModal}
      open={modalOpen}
      isDividerHidden
      headerBackgroundColor="transparent"
      headerButtonColor="black"
      showShadow={isDesktop}
      width={{ _: null, lg: '45%' }}
      headerButtonWithCircularBackground
      buttons={
        !showLeaveContent && {
          primary: (
            <Button widthType="full" variant="primary" size="tall" onClick={closeModal}>
              View menu
            </Button>
          ),
        }
      }
      childrenContainerStyle={{ marginBottom: '0' }}
      buttonContainerStyle={{ position: 'relative', backgroundColor: 'transparent' }}
      variant="side"
      backgroundColor={colors.warmWhite2}
      hideBottomDecal={false}
    >
      {showLeaveContent ? leaveContent : shareContent}
    </Modal>
  );
};

TabOrderShareModal.propTypes = {
  modalOpen: PropTypes.bool.isRequired,
  handleModalClose: PropTypes.func.isRequired,
};

const ConnectedTabOrderShareModal = observer(TabOrderShareModal);

export { ConnectedTabOrderShareModal as TabOrderShareModal };
